/**
 * ImageWithBoundingBox component
 *
 * NOTE: if "clickable" is true, this should only be used for Out of Stock.
 * It may get too complicated to use for other cases.
 */
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Image, Box } from "@mantine/core";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { LETTER_INDEX_MAP } from "./config/letterIndexMap";
import { useImageCrop } from "./hooks/useImageCrop";
import { ImageLoading } from "./ImageLoading";
import {
  orderCorners,
  isAxisAlignedQuad,
  toSvgPolygonPoints,
} from "./utils/obbGeometry";

export type Point = [number, number];

interface ImageWithBoundingBoxProps {
  src: string | undefined;
  alt: string;
  points?: { points: Point[] }[];
  cropPoints?: Point[];
  variant?: "proposal" | "empty";
  className?: string;
  style?: React.CSSProperties;
  enableZoom?: boolean;
  holdKeysForZoom?: boolean;
  initialZoom?: number;
  minZoom?: number;
  extraBoxContent?: React.ReactNode;
  activeBox?: number | null;
  clickable?: boolean;
  selectedBoxes?: number[];
  modelPredictions?: number[];
  onBoxClick?: (boxIndex: number) => void;
  matches?: number[];
  matchReferenceIndexes?: number[];
  referenceImageIndex?: number;
  enablePulsing?: boolean;
  crop?: boolean;
}

/**
 * Component that displays an image with a bounding box overlay
 * Supports image cropping based on points and different bounding box variants
 */
export const ImageWithBoundingBox: React.FC<ImageWithBoundingBoxProps> = ({
  src,
  alt,
  points = [],
  variant = "proposal",
  className = "",
  style = {},
  enableZoom = true,
  holdKeysForZoom,
  initialZoom = 1,
  minZoom = 1,
  extraBoxContent,
  clickable = false,
  selectedBoxes = [],
  onBoxClick,
  activeBox,
  modelPredictions,
  matches,
  matchReferenceIndexes,
  referenceImageIndex,
  enablePulsing = false,
  cropPoints,
  crop = false,
}) => {
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const positionedForSrcRef = useRef<string | null | undefined>(undefined);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExtraBoxVisible, setIsExtraBoxVisible] = useState(true);

  // crop
  // a lot of logic is based on the result here
  const cropResult = useImageCrop(src, points, cropPoints, crop);

  // Calculate bounding box from points
  const boundingBoxes = useMemo(
    () =>
      points.map((p) => {
        if (p.points.length === 0) return null;

        // If we have cropPoints, we need to adjust the bounding box calculations
        if (
          crop &&
          cropResult?.cropDimensions &&
          cropResult?.originalDimensions
        ) {
          // Calculate the crop area in percentage terms
          const cropMinX = Math.min(
            ...(cropResult.cropPoints?.map((point) => point[0]) ?? []),
          );
          const cropMaxX = Math.max(
            ...(cropResult.cropPoints?.map((point) => point[0]) ?? []),
          );
          const cropMinY = Math.min(
            ...(cropResult.cropPoints?.map((point) => point[1]) ?? []),
          );
          const cropMaxY = Math.max(
            ...(cropResult.cropPoints?.map((point) => point[1]) ?? []),
          );

          // Adjust the bounding box points relative to the crop area.
          // Kept unfiltered for the quad outline — dropping out-of-bounds
          // corners would deform the polygon (the container clips instead).
          const adjustedCorners = p.points.map(([x, y]): Point => {
            // Convert from original image coordinates to cropped image coordinates
            const adjustedX = (x - cropMinX) / (cropMaxX - cropMinX);
            const adjustedY = (y - cropMinY) / (cropMaxY - cropMinY);
            return [adjustedX, adjustedY];
          });
          // The AABB keeps only in-bounds corners, as before
          const adjustedPoints = adjustedCorners.filter(
            ([x, y]) => x >= 0 && x <= 1 && y >= 0 && y <= 1,
          );

          if (adjustedPoints.length === 0) return null;

          const minX = Math.min(
            ...adjustedPoints.map((point) => point[0] * 100),
          );
          const maxX = Math.max(
            ...adjustedPoints.map((point) => point[0] * 100),
          );
          const minY = Math.min(
            ...adjustedPoints.map((point) => point[1] * 100),
          );
          const maxY = Math.max(
            ...adjustedPoints.map((point) => point[1] * 100),
          );

          return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            centerX: (minX + maxX) / 2,
            centerY: (minY + maxY) / 2,
            corners: orderCorners(adjustedCorners).map(
              ([x, y]): Point => [x * 100, y * 100],
            ),
            isAxisAligned: isAxisAlignedQuad(p.points),
          };
        } else {
          // Original calculation for non-cropped images
          const minX = Math.min(...p.points.map((p) => p[0] * 100));
          const maxX = Math.max(...p.points.map((p) => p[0] * 100));
          const minY = Math.min(...p.points.map((p) => p[1] * 100));
          const maxY = Math.max(...p.points.map((p) => p[1] * 100));

          return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            centerX: (minX + maxX) / 2,
            centerY: (minY + maxY) / 2,
            corners: orderCorners(p.points).map(
              ([x, y]): Point => [x * 100, y * 100],
            ),
            isAxisAligned: isAxisAlignedQuad(p.points),
          };
        }
      }),
    [
      points,
      crop,
      cropPoints,
      cropResult?.cropDimensions,
      cropResult?.originalDimensions,
      cropResult?.cropPoints,
    ],
  );
  const boundingBox = boundingBoxes[0];

  const calculateConstrainedPosition = (
    x: number,
    y: number,
    scale: number,
  ) => {
    if (!boxRef.current || !imageDimensions) return { x, y };

    const containerWidth = boxRef.current.offsetWidth;
    const containerHeight = boxRef.current.offsetHeight;

    // Calculate the scaled image dimensions
    const scaledImageWidth = containerWidth * scale;
    const scaledImageHeight = containerHeight * scale;

    // Calculate the maximum allowed movement to keep image in frame
    const maxX = Math.max(0, scaledImageWidth - containerWidth);
    const maxY = Math.max(0, scaledImageHeight - containerHeight);

    // Clamp the coordinates within bounds
    const constrainedX = Math.max(-maxX, Math.min(maxX, x));
    const constrainedY = Math.max(-maxY, Math.min(maxY, y));

    return { x: Math.min(constrainedX, 0), y: Math.min(constrainedY, 0) };
  };

  // The actually-rendered image URL — updates only after the async
  // crop/sign work finishes, so `boundingBox` and `imageDimensions` are
  // guaranteed to be in sync with it. Keying the positioning effect on
  // this avoids both (a) running with stale crop state mid-transition and
  // (b) resetting zoom on intra-task re-renders that churn `points` refs.
  const activeSrc = crop ? cropResult?.croppedImageUrl : src;

  // here is where we zoom and position the image on load
  useEffect(() => {
    const scale = initialZoom;
    if (
      boundingBox &&
      transformRef.current &&
      boxRef.current &&
      imageDimensions
    ) {
      if (positionedForSrcRef.current === activeSrc) return;

      // Calculate pixel position
      const xDistanceFromTopLeft =
        (boundingBox.centerX / 100) * boxRef.current.offsetWidth;
      const yDistanceFromTopLeft =
        (boundingBox.centerY / 100) * boxRef.current.offsetHeight;

      const rawX = -(
        xDistanceFromTopLeft * scale -
        boxRef.current.offsetWidth / 2
      );
      const rawY = -(
        yDistanceFromTopLeft * scale -
        boxRef.current.offsetHeight / 2
      );

      // Apply boundary constraints
      const { x, y } = calculateConstrainedPosition(rawX, rawY, scale);

      transformRef.current.setTransform(x, y, scale, 0);
      positionedForSrcRef.current = activeSrc;

      // Mark as positioned after transform is applied
      // Use a small delay to ensure the transform has been applied
      requestAnimationFrame(() => {
        setIsLoading(false);
      });
    } else if (!crop && (imageDimensions || !points.length)) {
      // If no crop and no bounding box, still wait for image dimensions before showing
      setIsLoading(false);
    }
  }, [boundingBox, imageDimensions, activeSrc]);

  useEffect(() => {
    if (cropResult?.originalDimensions) {
      setImageDimensions(cropResult.originalDimensions);
    }
  }, [cropResult?.originalDimensions]);

  const getMatchContent = (boxIndex: number) => {
    if (matches) {
      for (let i = 0; i < matches.length; i++) {
        if (
          matches[i] === boxIndex &&
          matchReferenceIndexes?.[i] === referenceImageIndex
        ) {
          const letter =
            LETTER_INDEX_MAP[i.toString() as keyof typeof LETTER_INDEX_MAP];
          return (
            <div
              className="flex items-center justify-center w-full h-full"
              style={{ color: "#fff", fontWeight: 500 }}
            >
              {letter}
            </div>
          );
        }
      }
    }
    return null;
  };

  /**
   * This function shows/hides "extraBoxContent" based on the active box and the transformation state.
   * It is used to show/hide the product preview tooltip.
   */
  const checkExtraBoxVisibility = useCallback(() => {
    // Guard clauses: exit if refs aren't ready or no box is active
    if (
      activeBox === null ||
      activeBox === undefined ||
      !transformRef.current ||
      !boxRef.current
    ) {
      setIsExtraBoxVisible(true); // Reset to visible when no box is active
      return;
    }
    const activeBoundingBox = boundingBoxes[activeBox];
    if (!activeBoundingBox) return;

    // Get current transformation state and container dimensions
    const { scale, positionX, positionY } =
      transformRef.current.instance.transformState;
    const container = boxRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    // Calculate the viewport boundaries in the content's coordinate system (in pixels)
    const viewLeft = -positionX / scale;
    const viewTop = -positionY / scale;
    const viewRight = viewLeft + containerWidth / scale;
    const viewBottom = viewTop + containerHeight / scale;

    // Calculate the active box's boundaries in the content's coordinate system (in pixels)
    const boxLeft = (activeBoundingBox.x / 100) * containerWidth;
    const boxTop = (activeBoundingBox.y / 100) * containerHeight;
    const boxRight = boxLeft + (activeBoundingBox.width / 100) * containerWidth;
    const boxBottom =
      boxTop + (activeBoundingBox.height / 100) * containerHeight;

    // Check if the two rectangles overlap
    const isVisible =
      boxLeft < viewRight &&
      boxRight > viewLeft &&
      boxTop < viewBottom &&
      boxBottom > viewTop;

    setIsExtraBoxVisible(isVisible);
  }, [activeBox, boundingBoxes]);

  // Per-variant stroke/fill, shared by the AABB div border and the rotated
  // quad's SVG polygon so the two render paths can't drift apart
  const getVariantStyle = (boxIndex: number) => {
    if (variant === "proposal" && modelPredictions?.includes(boxIndex)) {
      return {
        strokeColor: "#3b82f6",
        strokeWidth: 1.5,
        selectedFill: "rgba(59, 130, 246, 0.4)",
        borderRadius: "5px" as const,
      };
    } else if (variant === "proposal") {
      return {
        strokeColor: "#FF00FF",
        strokeWidth: 1,
        selectedFill: "rgba(255, 0, 255, 0.4)",
        borderRadius: undefined,
      };
    } else {
      return {
        strokeColor: "#ef4444",
        strokeWidth: 1.5,
        selectedFill: "rgba(239, 68, 68, 0.4)",
        borderRadius: undefined,
      };
    }
  };

  // Calculate bounding box styles
  const getBoundingBoxStyle = (
    box: any,
    boxIndex: number,
    renderAsQuad: boolean,
  ) => {
    if (!box || !imageDimensions || !boxRef.current) return {};

    // const pxWidth = (boundingBox.width / 100) * (boxRef.current?.offsetWidth ?? 0 / imageDimensions?.width);
    // const pxHeight = (boundingBox.height / 100) * (boxRef.current?.offsetHeight ?? 0 / imageDimensions?.height);
    // const originalBoxWidth = boundingBox.width * imageDimensions.width;
    // const originalBoxHeight = boundingBox.height * imageDimensions.height;
    // const pxHeight = pxWidth * originalBoxHeight / originalBoxWidth;

    const isSelected = selectedBoxes.includes(boxIndex);
    // const isPrediction =
    const baseStyle: React.CSSProperties = {
      position: "absolute",
      top: `${box.y}%`,
      left: `${box.x}%`,
      // width: `${pxWidth}px`,
      // height: `${pxHeight}px`,
      width: `${box.width}%`,
      height: `${box.height}%`,
      pointerEvents: "auto",
      zIndex: 10,
    };

    // Add pulsing animation if enabled
    if (enablePulsing) {
      baseStyle.animation = "pulse 0.5s ease-in-out infinite";
    }

    // Rotated quads draw their stroke/fill via the SVG polygon child; the
    // div stays as the (AABB) hit area and content anchor
    if (renderAsQuad) return baseStyle;

    const { strokeColor, strokeWidth, selectedFill, borderRadius } =
      getVariantStyle(boxIndex);
    return {
      ...baseStyle,
      border: `${strokeWidth}px solid ${strokeColor}`,
      ...(borderRadius ? { borderRadius } : {}),
      backgroundColor: isSelected ? selectedFill : "transparent",
    };
  };

  const imageContent = (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* CSS for pulsing animation */}
      {enablePulsing && (
        <style>
          {`
            @keyframes pulse {
              0% {
                transform: scale(1);
              }
              50% {
                transform: scale(1.2);
              }
              100% {
                transform: scale(1);
              }
            }
          `}
        </style>
      )}
      <div>
        {activeSrc ? (
          <Image
            src={activeSrc}
            alt={alt}
            radius="md"
            className="border border-gray-200 shadow-sm"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
            onError={() => {
              setIsLoading(false);
            }}
          />
        ) : (
          // No usable src yet (crop still (re)computing on a cache miss).
          // Render an empty placeholder instead of an <img> with an empty src,
          // which flashes the browser's broken-image icon before the crop
          // resolves. The loading overlay sits on top of this.
          <div style={{ width: "100%", height: "100%" }} />
        )}
      </div>

      {boundingBoxes?.length > 0 &&
        boundingBoxes.map((box, i) => {
          // Oriented (rotated) detections render as an SVG polygon over the
          // true corners; axis-aligned ones keep the plain bordered div
          const renderAsQuad =
            !!box &&
            !box.isAxisAligned &&
            box.corners.length === 4 &&
            box.width > 0 &&
            box.height > 0;
          const variantStyle = getVariantStyle(i);
          return (
            <div
              onClick={() => {
                if (!clickable) return;
                onBoxClick?.(i);
              }}
              key={i}
              style={getBoundingBoxStyle(box, i, renderAsQuad)}
            >
              {renderAsQuad && box && (
                <svg
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                    pointerEvents: "none",
                    display: "block",
                  }}
                >
                  <polygon
                    points={toSvgPolygonPoints(
                      // Corners are in image-percent space; map them into
                      // the div-local 0-100 viewBox
                      box.corners.map(
                        ([cx, cy]): Point => [
                          ((cx - box.x) / box.width) * 100,
                          ((cy - box.y) / box.height) * 100,
                        ],
                      ),
                      1,
                    )}
                    fill={
                      selectedBoxes.includes(i)
                        ? variantStyle.selectedFill
                        : "none"
                    }
                    stroke={variantStyle.strokeColor}
                    strokeWidth={variantStyle.strokeWidth}
                    vectorEffect="non-scaling-stroke"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {getMatchContent(i)}
              {activeBox === i && isExtraBoxVisible && extraBoxContent}
            </div>
          );
        })}
    </div>
  );

  if (!enableZoom) {
    return (
      <Box
        className={`relative ${className}`}
        ref={boxRef}
        style={{
          overflow: "hidden",
          width: "100%",
          height: "auto",
          // aspectRatio: "3/4",
          ...style,
        }}
      >
        {imageContent}
      </Box>
    );
  }

  // smoothStep scales trackpad zoom speed (library default 0.001 is so small
  // that, combined with the library rounding scale to 3 decimals, many pinch
  // events round to no change — which reads as stutter). Raising it keeps each
  // event's increment above the rounding threshold.
  const wheelConfig = {
    smoothStep: 0.01,
    ...(holdKeysForZoom
      ? { step: 0.2, activationKeys: ["Control", "Meta"] }
      : {}),
  };

  return (
    <Box
      className={`relative ${className}`}
      ref={boxRef}
      style={{
        width: "100%",
        // height: '300px',
        // overflow: 'hidden',
        aspectRatio: crop ? "1/1" : "auto",
        ...style,
      }}
    >
      {/* Loading overlay. Also shown synchronously whenever a crop has no
          usable src yet (cache miss / recompute), so a task swap never flashes
          a broken-image icon or blank box before the overlay's post-paint
          effect runs. Cache hits set activeSrc synchronously, so no flash. */}
      {(isLoading || (crop && !activeSrc)) && <ImageLoading />}

      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={minZoom}
        maxScale={Infinity}
        wheel={wheelConfig}
        pinch={{ step: 5 }}
        doubleClick={{ disabled: true }}
        onTransformed={checkExtraBoxVisibility}
        panning={{ velocityDisabled: true }}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          contentStyle={{ width: "100%", height: "100%" }}
        >
          {imageContent}
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
};
